/**
 * Enhanced global image cache that:
 * 1. Uses in-memory cache during the session
 * 2. Persists to IndexedDB for permanent storage
 * 3. Falls back to localStorage if IndexedDB isn't available
 * 4. Converts images to base64 for instant loading
 */

// Global in-memory cache that persists during the entire session
const memoryCache = new Map();

// IndexedDB database name and store
const DB_NAME = 'NukeproofImageCache';
const STORE_NAME = 'images';
const DB_VERSION = 1;

// Initialize the IndexedDB database
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      console.warn('IndexedDB not supported, falling back to localStorage');
      resolve(null);
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('IndexedDB error:', event.target.error);
      resolve(null);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        store.createIndex('url', 'url', { unique: true });
      }
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };
  });
};

// Pending promises for in-flight requests to prevent double processing
const pendingRequests = new Map();

// Convert an image to base64
const imageToBase64 = (url) => {
  return new Promise((resolve, reject) => {
    // If it's already a data URL, return it as is
    if (url.startsWith('data:')) {
       resolve({ url, base64: url });
       return;
    }

    const img = new Image();
    
    // Enable cross-origin loading
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      
      try {
        const base64 = canvas.toDataURL('image/webp');
        resolve({ url, base64, width: img.width, height: img.height });
      } catch (e) {
        console.warn(`Failed to convert to base64: ${url}`, e);
        resolve({ url, base64: null });
      }
    };
    
    img.onerror = () => {
      console.warn(`Failed to load for base64 conversion: ${url}`);
      resolve({ url, base64: null });
    };
    
    // NO cache busting timestamp here - we WANT to use the browser cache!
    // If the browser has it, this will load instantly from disk/memory cache.
    img.src = url;
  });
};

// Save image to IndexedDB
const saveToIndexedDB = async (imageData) => {
  try {
    const db = await initDB();
    if (!db) return false;
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(imageData);
      
      request.onsuccess = () => resolve(true);
      request.onerror = (event) => {
        console.error('Error saving to IndexedDB:', event.target.error);
        resolve(false);
      };
    });
  } catch (e) {
    console.error('Error in saveToIndexedDB:', e);
    return false;
  }
};

// Get image from IndexedDB
const getFromIndexedDB = async (url) => {
  try {
    const db = await initDB();
    if (!db) return null;
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(url);
      
      request.onsuccess = () => {
        resolve(request.result);
      };
      
      request.onerror = (event) => {
        console.error('Error getting from IndexedDB:', event.target.error);
        resolve(null);
      };
    });
  } catch (e) {
    console.error('Error in getFromIndexedDB:', e);
    return null;
  }
};

// Fallback to localStorage if IndexedDB isn't available
const saveToLocalStorage = (key, value) => {
  try {
    // Store just the URL -> base64 mapping to save space
    const data = {
      url: value.url,
      base64: value.base64
    };
    localStorage.setItem(`nukeproof_img_${key.replace(/[^a-zA-Z0-9]/g, '_')}`, JSON.stringify(data));
    return true;
  } catch (e) {
    console.error('Error saving to localStorage:', e);
    return false;
  }
};

const getFromLocalStorage = (key) => {
  try {
    const data = localStorage.getItem(`nukeproof_img_${key.replace(/[^a-zA-Z0-9]/g, '_')}`);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error('Error getting from localStorage:', e);
    return null;
  }
};

/**
 * Check if an image is in the cache
 * @param {string} url - URL of the image to check
 * @returns {Promise<Object|null>} - Cached image data or null if not cached
 */
export const getFromCache = async (url) => {
  // Check in-memory cache first
  if (memoryCache.has(url)) {
    return memoryCache.get(url);
  }

  // Check IndexedDB next
  const dbData = await getFromIndexedDB(url);
  if (dbData && dbData.base64) {
    // Store in memory cache for future use
    memoryCache.set(url, dbData);
    return dbData;
  }

  // Fall back to localStorage
  const localData = getFromLocalStorage(url);
  if (localData && localData.base64) {
    // Store in memory cache for future use
    memoryCache.set(url, localData);
    return localData;
  }

  return null;
};

/**
 * Preload an image and cache it for fast access
 * @param {string} url - URL of the image to preload
 * @returns {Promise<Object>} - Object with the url and base64 data
 */
export const preloadAndCache = async (url) => {
  if (!url) return null;

  // Check if there is already a pending request for this URL to avoid double-fetching
  if (pendingRequests.has(url)) {
    return pendingRequests.get(url);
  }

  const promise = (async () => {
    try {
      // Check if already cached
      const cached = await getFromCache(url);
      if (cached && cached.base64) {
        return cached;
      }

      // Convert to base64 and cache
      const imageData = await imageToBase64(url);
      
      // Don't cache if conversion failed
      if (!imageData || !imageData.base64) {
        return null;
      }
      
      // Save to all storage mechanisms
      memoryCache.set(url, imageData);
      saveToIndexedDB(imageData).catch(console.error);
      
      // Defer localStorage to execute in next tick to avoid blocking main thread
      setTimeout(() => saveToLocalStorage(url, imageData), 0);
      
      return imageData;
    } catch (err) {
      console.error(`Error preloading ${url}:`, err);
      return null;
    } finally {
      pendingRequests.delete(url);
    }
  })();

  pendingRequests.set(url, promise);
  return promise;
};

/**
 * Use the cached image as a background for an element
 * @param {string} elementId - ID of the element to set the background for
 * @param {string} url - URL of the image to use
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export const setBackgroundFromCache = async (elementId, url) => {
  if (!elementId || !url) return false;

  const element = document.getElementById(elementId);
  if (!element) return false;

  // Try to get from cache first
  const cached = await getFromCache(url);
  
  if (cached && cached.base64) {
    // Set background directly using the base64 data
    element.style.backgroundImage = `url("${cached.base64}")`;
    return true;
  }

  // If not cached, set the original URL and try to cache for next time
  element.style.backgroundImage = `url("${url}")`;
  
  // Start caching in the background
  preloadAndCache(url).catch(console.error);
  
  return false;
};

/**
 * Preload multiple images at once
 * @param {string[]} urls - Array of image URLs to preload
 * @returns {Promise<Object[]>} - Array of cached image data
 */
export const preloadImages = async (urls) => {
  if (!urls || !urls.length) return [];
  
  // Use Promise.all for parallel loading
  return Promise.all(urls.map(url => preloadAndCache(url)));
};

/**
 * Force preload all background images in bike categories
 */
export const preloadAllBikeCategoryImages = () => {
  const elements = document.querySelectorAll('.option2');
  const urls = Array.from(elements)
    .map(el => el.dataset.imageSrc)
    .filter(Boolean);
    
  // Preload all urls
  if (urls.length > 0) {
    console.log(`Preloading ${urls.length} bike category images`);
    preloadImages(urls).then(results => {
      console.log(`Successfully cached ${results.filter(Boolean).length}/${urls.length} images`);
      
      // Now apply the cached images immediately
      elements.forEach(el => {
        const url = el.dataset.imageSrc;
        if (url) {
          setBackgroundFromCache(el.id, url);
        }
      });
    });
  }
  
  return urls.length;
};

const globalImageCache = {
  getFromCache,
  preloadAndCache,
  setBackgroundFromCache,
  preloadImages,
  preloadAllBikeCategoryImages
};

export default globalImageCache;
